import { useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingOrderCTA from "./components/FloatingOrderCTA";
import FloatingCateringCTA from "./components/FloatingCateringCTA";
import Home from "./pages/Home";
import Menu from "./pages/Menu";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Catering from "./pages/Catering";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import DoNotSell from "./pages/DoNotSell";
import LosGatos from "./pages/LosGatos";
import WelcomePopup from "./components/WelcomePopup";
import SpanishWelcomeBadge from "./components/SpanishWelcomeBadge";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/toaster";
import { pageView } from "./utils/analytics";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Disable smooth scroll for the route jump so it lands instantly at the top
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);
    // restore
    requestAnimationFrame(() => { html.style.scrollBehavior = prev; });
  }, [pathname]);
  return null;
}

/**
 * SPA route tracker — fires a `page_view` dataLayer event on EVERY client-side
 * route change so GTM (GTM-MG9Z3BS → GA4 G-PKT2YYSCQ0) can record it.
 *
 * The initial page_view is fired automatically by GTM's GA4 Configuration tag
 * (send_page_view=true, default). We must NOT fire another one from React on
 * initial mount, or GA4 would double-count page_view / session_start.
 *
 * StrictMode-safe: we track the previous pathname in a ref and only fire when
 * it actually changes. On the initial mount (and its StrictMode double-invoke),
 * prevPath is null → we just record the current path without firing.
 */
function RouteTracker() {
  const { pathname, search } = useLocation();
  const prevPath = useRef(null);
  useEffect(() => {
    const currentPath = pathname + search;
    if (prevPath.current === null) {
      prevPath.current = currentPath;
      return; // initial mount — GTM's GA4 Config already fires page_view
    }
    if (prevPath.current === currentPath) {
      return; // same route (e.g. StrictMode double-invoke) — nothing to do
    }
    prevPath.current = currentPath;
    const id = window.setTimeout(() => {
      pageView({
        path: currentPath,
        title: document.title,
        location: window.location.href,
      });
    }, 50);
    return () => window.clearTimeout(id);
  }, [pathname, search]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <div className="App min-h-screen bg-cream-50">
          <BrowserRouter>
            <ScrollToTop />
            <RouteTracker />
            <Navbar />
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/menu" element={<Menu />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/catering" element={<Catering />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/do-not-sell" element={<DoNotSell />} />
            <Route path="/los-gatos" element={<LosGatos />} />
          </Routes>
          <Footer />
          <FloatingOrderCTA />
          <FloatingCateringCTA />
          <WelcomePopup />
          <SpanishWelcomeBadge />
          <Toaster />
        </BrowserRouter>
      </div>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
